/**
 * Ravici "Side" controller.
 *
 * The storefront has two colourways: For Him (dark) and For Her (light). The active side lives on
 * `<html data-side>`; an inline script in layout/theme.liquid sets it before first paint from the
 * page context (collection handle / product tag), localStorage or the theme default. This module
 * handles everything that changes after load:
 *
 *  - `[data-ravici-set-side]`   hover (hero panes) or click (header links, panes) switches side
 *  - `[data-ravici-hero]`       pane hover grows the pane and moves the seam
 *  - `[data-ravici-countdown]`  live drop countdown from `data-target` (ISO date)
 *  - `[data-ravici-look-mode]`  Comfort / Train tabs in Shop the look
 *  - header inverse once the page is scrolled past 40px (`html[data-ravici-scrolled]`)
 *  - theme editor: selecting a block that belongs to one side switches to that side
 */
const STORAGE_KEY = 'ravici_side';
const SIDES = new Set(['him', 'her']);
const root = document.documentElement;

function setSide(side, { persist = true } = {}) {
  if (!SIDES.has(side) || root.dataset.side === side) return;
  root.dataset.side = side;
  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, side);
    } catch {
      /* storage unavailable */
    }
  }
  document.dispatchEvent(new CustomEvent('ravici:side', { detail: { side } }));
}

window.Ravici = Object.assign(window.Ravici || {}, { setSide, get side() { return root.dataset.side; } });

/* ---------- side triggers ---------- */
document.addEventListener('pointerenter', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const el = target.closest('[data-ravici-set-side][data-ravici-on-hover]');
  if (!el || window.matchMedia('(hover: none)').matches) return;
  setSide(el.dataset.raviciSetSide);
}, true);

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const el = target.closest('[data-ravici-set-side]');
  if (!el) return;
  const side = el.dataset.raviciSetSide;
  // Hero pane on a touch device: first tap switches side, second tap follows the link.
  if (el.hasAttribute('data-ravici-pane') && window.matchMedia('(hover: none)').matches && root.dataset.side !== side) {
    event.preventDefault();
    setSide(side);
    el.closest('[data-ravici-hero]')?.setAttribute('data-hover', side);
    return;
  }
  setSide(side);
});

/* ---------- split hero ---------- */
for (const hero of document.querySelectorAll('[data-ravici-hero]')) {
  for (const pane of hero.querySelectorAll('[data-ravici-pane]')) {
    pane.addEventListener('pointerenter', () => {
      if (window.matchMedia('(hover: none)').matches) return;
      hero.setAttribute('data-hover', pane.dataset.raviciSetSide || '');
    });
    pane.addEventListener('pointerleave', () => {
      if (window.matchMedia('(hover: none)').matches) return;
      hero.removeAttribute('data-hover');
    });
  }
}

/* ---------- seam label fit ---------- */
// The desktop seam label is rotated 90°, so its width must fit the hero height. The design shows
// "label · countdown · tagline" only when the hero is tall enough and keeps just the countdown
// otherwise; this does the same from a real measurement, so any label text works. In "fit" mode
// the text is first reduced (to 8px minimum) before the words are dropped.
const SEAM_FONT_PX = 9;
const SEAM_MIN_FONT_PX = 8;
const seamLabels = Array.from(document.querySelectorAll('[data-ravici-seam]'));
function fitSeamLabels() {
  for (const label of seamLabels) {
    const hero = label.closest('[data-ravici-hero]');
    if (!hero || !label.offsetParent) continue;
    const available = hero.clientHeight - 24;
    if (available <= 0) continue;
    label.removeAttribute('data-words-hidden');
    label.style.removeProperty('--ravici-seam-font');
    if (label.offsetWidth <= available) continue;
    if (label.dataset.raviciSeamWords === 'fit') {
      const px = Math.max(SEAM_MIN_FONT_PX, SEAM_FONT_PX * (available / label.offsetWidth));
      label.style.setProperty('--ravici-seam-font', px.toFixed(2) + 'px');
      if (label.offsetWidth <= available) continue;
      label.style.removeProperty('--ravici-seam-font');
    }
    label.setAttribute('data-words-hidden', '');
  }
}
if (seamLabels.length) {
  fitSeamLabels();
  window.addEventListener('resize', fitSeamLabels, { passive: true });
  document.fonts?.ready?.then(fitSeamLabels);
  document.addEventListener('shopify:section:load', fitSeamLabels);
}

/* ---------- countdown ---------- */
const pad = (n) => String(n).padStart(2, '0');
// Accepts ISO 8601 as well as the looser forms merchants tend to type: "2026-11-06 17:00",
// "2026-11-06" (midnight UTC), "11/06/2026 5:00 PM" (local time), or a bare ISO date without a zone.
function parseDropDate(raw) {
  const value = String(raw || '').trim();
  if (!value) return NaN;
  let t = Date.parse(value);
  if (!Number.isNaN(t)) return t;
  const iso = value.replace(' ', 'T');
  t = Date.parse(iso);
  if (!Number.isNaN(t)) return t;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return Date.parse(value + 'T00:00:00Z');
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(iso)) return Date.parse(iso + 'Z');
  return NaN;
}
const countdowns = Array.from(document.querySelectorAll('[data-ravici-countdown]'));
if (countdowns.length) {
  const tick = () => {
    const now = Date.now();
    for (const el of countdowns) {
      const target = parseDropDate(el.dataset.target);
      if (Number.isNaN(target)) {
        // Unreadable date: leave the placeholder timer in place and flag the element for the editor.
        el.setAttribute('data-invalid', '');
        continue;
      }
      let d = Math.max(0, target - now);
      const days = Math.floor(d / 864e5);
      d %= 864e5;
      const hours = Math.floor(d / 36e5);
      d %= 36e5;
      const minutes = Math.floor(d / 6e4);
      const seconds = Math.floor((d % 6e4) / 1000);
      if (el.hasAttribute('data-ravici-countdown-units')) {
        // Password page: four separate unit cells instead of one string.
        const values = { days, hours, minutes, seconds };
        for (const unit of el.querySelectorAll('[data-unit]')) {
          const v = pad(values[unit.dataset.unit] ?? 0);
          if (unit.textContent !== v) unit.textContent = v;
        }
        if (target <= now) {
          el.setAttribute('data-live', '');
          el.querySelector('[data-ravici-countdown-live]')?.removeAttribute('hidden');
        }
        continue;
      }
      let text = `${pad(days)}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
      if (target <= now && el.dataset.liveText) text = el.dataset.liveText;
      if (el.textContent !== text) el.textContent = text;
      if (target <= now) el.closest('[data-ravici-seam]')?.setAttribute('data-live', '');
      if (target <= now) fitSeamLabels();
    }
  };
  tick();
  setInterval(tick, 1000);
}

/* ---------- shop the look tabs ---------- */
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('[data-ravici-look-mode]');
  if (!(button instanceof HTMLElement)) return;
  const section = button.closest('[data-ravici-looks]');
  if (!section) return;
  section.setAttribute('data-look-mode', button.dataset.raviciLookMode || 'comfort');
  for (const tab of section.querySelectorAll('[data-ravici-look-mode]')) {
    tab.setAttribute('aria-pressed', String(tab === button || tab.dataset.raviciLookMode === button.dataset.raviciLookMode));
  }
});

/* ---------- header inverse on scroll ---------- */
// Horizon scrolls the page inside .page-wrapper on desktop and on window on mobile; watch both.
const pageWrapper = document.querySelector('.page-wrapper');
let scrolled = null;
const onScroll = () => {
  const offset = Math.max(window.scrollY || 0, pageWrapper?.scrollTop || 0);
  const next = offset > 40;
  if (next === scrolled) return;
  scrolled = next;
  if (next) root.setAttribute('data-ravici-scrolled', '');
  else root.removeAttribute('data-ravici-scrolled');
};
onScroll();
window.addEventListener('scroll', onScroll, { passive: true });
pageWrapper?.addEventListener('scroll', onScroll, { passive: true });

/* ---------- theme editor ---------- */
if (window.Shopify?.designMode) {
  const follow = (event) => {
    const el = event.target instanceof Element ? event.target.closest('[data-ravici-side]') : null;
    if (el instanceof HTMLElement && el.dataset.raviciSide) setSide(el.dataset.raviciSide, { persist: false });
  };
  document.addEventListener('shopify:block:select', follow);
  document.addEventListener('shopify:section:select', follow);
}
